import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref, shallowRef } from "vue";

import { useStore } from "@/stores";
import { useRouter } from "vue-router/composables";
import {
  API_BIS_REPORT_CUSTOMIZED,
  API_BIS_REPORT_CUSTOMIZED_WITH_ID,
  API_BIS_REPORT_MODEL,
  API_BIS_REPORT_STAT,
  API_BIS_CATEGORY_TREE,
} from "@/configs/Apis";
import { showConfirm, showSuccess } from "@/utils/UI";
import { isEmptyOrWhiteSpace } from "@/utils/Common";
import { QueryFieldGroup } from "@/app-data/patent-query";
import { ReportResult, ChartType } from "@/app-data/report/entities/reportEntity";
import { SYSTEM_REPORT_TREE } from "@/app-data/report/models";
import DuduAjax from "@/utils/DuduAjax";

import ReportChart from "./ReportChart.vue";
import ReportTable from "./ReportTable.vue";
import ReportFilter from "./ReportFilter.vue";
import AdvSearch from "@/views/components/AdvSearch.vue";

import * as charts from "@/app-data/report/charts";
import StatisticsPreview from "@/views/report/StatisticsPreview.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ReportIndex',
  setup(__props) {

const store = useStore();
const router = useRouter();

const reportTree = shallowRef<any>(null);
const reportForm = shallowRef<any>(null);
const reportChartForm = shallowRef<any>(null);
const advt = shallowRef<any>(null);

const reportTreeData = ref<Array<any>>([]);

const defaultProps: any = {
  children: "children",
  label: "label",
};

const expandedKeys = ref<Array<number>>([]);
const checkedKeys = ref<Array<number>>([]);
const treeStyles = ref<any>({});

const currentNode = ref<any>(null);

const currentChartOption = ref<any>({});
const currentDefinition = ref<any>({});
const currentReportTitle = ref("");
const currentDatas = ref<ReportResult>({
  colCount: 0,
  colDimNodes: [],
  dataValues: [],
  rowColIndexes: [],
  rowCount: 0,
  rowDimNodes: [],
  totalIndex: 0,
});

const appendFields = ref<Array<QueryFieldGroup>>([]);

const updateDataByFilter = ref(false);

const showReportEditWin = ref(false);

const currentReport = ref<any>({});
const initReportEntity: any = {
  id: 0,
  name: "",
  rowDim1Name: "",
  rowDim1Limit: 20,
  rowDim1Total: true,
  rowDim1Sub: true,
  rowDim2Name: "",
  rowDim2Limit: 20,
  rowDim2Total: true,
  rowDim2Sub: true,
  colDim1Name: "",
  colDim1Limit: 20,
  colDim1Total: true,
  colDim1Sub: true,
  measure1Name: "count",
  measure1MinValue: 0,
  chartType: "",
  chartTitle: "",
  chartSubTitle: "",
  xaxisTitle: "",
  yaxisTitle: "",
  hideTitle: false,
  hideAxisTitle: true,
  hideLabel: false,
  hideLegend: false,
  hideToolbox: false,
  condition: JSON.stringify({ items: [], relation: "" }),
};

const dimGroups = ref<Array<any>>([]);

const allMeasures = ref<Array<any>>([]);

const customizedReports = ref<any>({});

const reportEditStep = ref(0);

const helpText: string =
  "a. 1个行维度支持: 折线图/柱状图/饼图<br/>" +
  "b. 1个行维度 + 1个列维度, 支持: 折线图/柱状图/气泡图/热力图<br/>" +
  "c. 2个行维度 + 1个列维度, 支持: 气泡图<br/>" +
  "d. 2个行维度, 支持: 折线图/柱状图";

const projectId = computed(() => {
  const pInfo: any = (store.state as any).project.info || {};
  return pInfo.id || 0;
});

const chartTypes = computed(() => {
  // a. 1个行维度支持: 折线图/柱状图/饼图<br/>
  // b. 1个行维度 + 1个列维度, 支持: 折线图/柱状图/气泡图/热力图<br/>
  // c. 2个行维度 + 1个列维度, 支持: 气泡图<br/>
  // d. 2个行维度, 支持: 折线图/柱状图

  let lineDisabled = true;
  let barDisabled = true;
  let pieDisabled = true;
  let matrixPieDisabled = true;
  let heatMapDisabled = true;

  if (currentReport.value.rowDim1Name !== "") {
    if (currentReport.value.rowDim2Name !== "") {
      if (currentReport.value.colDim1Name !== "") {
        matrixPieDisabled = false;
      } else {
        lineDisabled = false;
        barDisabled = false;
      }
    } else {
      if (currentReport.value.colDim1Name !== "") {
        lineDisabled = false;
        barDisabled = false;
        matrixPieDisabled = false;
        heatMapDisabled = false;
      } else {
        lineDisabled = false;
        barDisabled = false;
        pieDisabled = false;
      }
    }
  }

  return [
    {
      value: "LINE",
      label: "折线图",
      disabled: lineDisabled,
    },
    {
      value: "BAR",
      label: "柱状图",
      disabled: barDisabled,
    },
    {
      value: "PIE",
      label: "饼图",
      disabled: pieDisabled,
    },
    {
      value: "MATRIXPIE",
      label: "气泡图",
      disabled: matrixPieDisabled,
    },
    {
      value: "HEATMAP",
      label: "热力图",
      disabled: heatMapDisabled,
    },
  ];
});

const reportRules = computed(() => {
  const validateRepeatDim = (rule: any, value: any, callback: any) => {
    const repeated =
      (currentReport.value.rowDim1Name !== "" &&
        currentReport.value.rowDim2Name !== "" &&
        currentReport.value.rowDim1Name === currentReport.value.rowDim2Name) ||
      (currentReport.value.rowDim1Name !== "" &&
        currentReport.value.colDim1Name !== "" &&
        currentReport.value.rowDim1Name === currentReport.value.colDim1Name) ||
      (currentReport.value.rowDim2Name !== "" &&
        currentReport.value.colDim1Name !== "" &&
        currentReport.value.rowDim2Name === currentReport.value.colDim1Name);
    if (repeated) {
      callback(new Error("维度不能重复"));
    } else {
      callback();
    }
  };
  const validateChartType = (rule: any, value: any, callback: any) => {
    for (let i = 0; i < chartTypes.value.length; i++) {
      if (chartTypes.value[i].value === currentReport.value.chartType && chartTypes.value[i].disabled) {
        currentReport.value.chartType = "";
        callback(new Error("图表类型不合法"));
        return;
      }
    }
    callback();
  };
  return {
    name: [{ required: true, message: "请输入名称", trigger: "blur" }],
    rowDim1Name: [
      { required: true, message: "请选择", trigger: "change" },
      { validator: validateRepeatDim, trigger: "change" },
    ],
    rowDim2Name: [{ validator: validateRepeatDim, trigger: "change" }],
    colDim1Name: [{ validator: validateRepeatDim, trigger: "change" }],
    measure1Name: [{ required: true, message: "请选择", trigger: "change" }],
    chartType: [
      { required: true, message: "请选择", trigger: "change" },
      { validator: validateChartType, trigger: "change" },
    ],
  };
});

const colseEditReport = () => {
  reportEditStep.value = 0;
  showReportEditWin.value = false;
};

const editReportPreStep = () => {
  if (reportEditStep.value === 1) {
    reportEditStep.value = 0;
  } else if (reportEditStep.value === 2) {
    reportEditStep.value = 1;
  }
};

const editReportNextStep = async () => {
  if (reportEditStep.value === 0) {
    const isValid = await reportForm.value.validate().catch(() => false);
    if (!isValid) {
      return;
    }
    reportEditStep.value = 1;
  } else if (reportEditStep.value === 1) {
    const isValid = await reportChartForm.value.validate().catch(() => false);
    if (!isValid) {
      return;
    }
    reportEditStep.value = 2;
  }
};

const onReportNameChange = () => {
  if (currentReport.value.chartTitle === "") {
    currentReport.value.chartTitle = currentReport.value.name;
  }
};

const loadCategoryTree = async () => {
  const data: any = await DuduAjax.get<Array<any>>(
    { url: API_BIS_CATEGORY_TREE, path: { projectId: projectId.value } },
    {},
    false
  ).catch(() => "");
  const treeData: Array<any> = [];
  const map = new Map<string, any>();
  if (data && data.length) {
    data.forEach((item: any) => {
      if (item.treeLevel === 0) {
        return;
      }

      let treeItem;
      if (map.has(item.id)) {
        treeItem = map.get(item.id);
        if (isEmptyOrWhiteSpace(treeItem.title)) {
          Object.assign(treeItem, item);
        }
      } else {
        treeItem = { name: item.name, ...item };
        map.set(item.id, treeItem);
      }

      if (item.treeLevel === 1) {
        treeData.push(treeItem);
      } else {
        const pId = item.parentId;
        if (map.has(pId)) {
          const pItem = map.get(pId);
          if (!Object.prototype.hasOwnProperty.call(pItem, "children")) {
            pItem.children = [];
          }
          pItem.children.push(treeItem);
        } else {
          map.set(pId, { children: [treeItem] });
        }
      }
    });
  }

  const categoryQueryFields = [];
  categoryQueryFields.push(
    ...treeData.map((field: any) => ({
      name: `category${field.id}`,
      label: field.name,
      type: "tree",
      treeData: JSON.parse(JSON.stringify(field.children || [])),
    }))
  );
  appendFields.value.push({
    name: "自定义分类",
    fields: categoryQueryFields,
  } as any);

  const customfields = store.getters["project/customfields"];
  if (customfields && customfields.length) {
    const customQueryFields = [];
    customQueryFields.push(
      ...customfields.map((field: any) => {
        const item: any = { name: field.fieldName.replace("_", ""), label: field.label };
        if (field.fieldType === "ENUM") {
          item.type = "enum";
          item.enumValues = [];
          if (!isEmptyOrWhiteSpace(field.enumValues)) {
            item.enumValues.push(...field.enumValues.split(/[\r\n;]/g));
          }
        } else {
          item.type = "text";
        }
        return item;
      })
    );
    appendFields.value.push({
      name: "标引字段",
      fields: customQueryFields,
    } as any);
  }
};

const loadReportModel = async () => {
  const model: any = await DuduAjax.get<any>({ url: API_BIS_REPORT_MODEL, path: { projectId: projectId.value } }).catch(
    () => ""
  );
  if (!model) {
    return;
  }
  dimGroups.value = model.dimensions;
  for (let i = 0; i < dimGroups.value.length; i++) {
    for (let j = 0; j < dimGroups.value[i].dims.length; j++) {
      dimGroups.value[i].dims[j].rname = dimGroups.value[i].dims[j].name;
      if (dimGroups.value[i].dims[j].dateUnit) {
        if (dimGroups.value[i].dims[j].dateUnit === "YEAR") {
          dimGroups.value[i].dims[j].rname = dimGroups.value[i].dims[j].rname + "__y";
        } else if (dimGroups.value[i].dims[j].dateUnit === "MONTH") {
          dimGroups.value[i].dims[j].rname = dimGroups.value[i].dims[j].rname + "__m";
        }
      }
    }
  }
  allMeasures.value = model.measures;
};

const initReportTree = async (id?: number) => {
  const datas: any = await DuduAjax.get<any>({
    url: API_BIS_REPORT_CUSTOMIZED,
    path: { projectId: (store.state as any).project.info.id },
  }).catch(() => "");
  const customizedReport: any = {
    id: 1000,
    label: "自定义分析",
    children: [],
  };

  for (let i = 0; i < datas.length; i++) {
    const rpt = datas[i];
    let chart;
    if (!Object.prototype.hasOwnProperty.call(rpt, "chartOption")) {
      continue;
    }
    const chartOption: any = JSON.parse(rpt.chartOption);
    const definition: any = JSON.parse(rpt.definition);
    if (definition.measures.length > 1) {
      definition.measures = [definition.measures[0]];
    }

    if (definition.rowDims.length === 1) {
      if (definition.colDims.length === 0) {
        // a. 1个行维度支持: 折线图/柱状图/饼图<br/>
        if (chartOption.chartType === "LINE") {
          chart = charts.lineAndBar;
          chartOption.chartType = ChartType.LINE;
        } else if (chartOption.chartType === "BAR") {
          chart = charts.lineAndBar;
          chartOption.chartType = ChartType.BAR;
        } else if (chartOption.chartType === "PIE") {
          chart = charts.pie;
          chartOption.chartType = ChartType.PIE;
        } else {
          continue;
        }
      } else if (definition.colDims.length === 1) {
        // b. 1个行维度 + 1个列维度, 支持: 折线图/柱状图/气泡图/热力图<br/>
        if (chartOption.chartType === "LINE") {
          chart = charts.lineAndBar2D;
          chartOption.chartType = ChartType.LINE;
        } else if (chartOption.chartType === "BAR") {
          chart = charts.lineAndBar2D;
          chartOption.chartType = ChartType.BAR;
        } else if (chartOption.chartType === "MATRIXPIE") {
          chart = charts.matrixPie2D;
          chartOption.chartType = ChartType.MATRIXPIE;
        } else if (chartOption.chartType === "HEATMAP") {
          chart = charts.headMap2D;
          chartOption.chartType = ChartType.HEATMAP;
        } else {
          continue;
        }
      }
    } else if (definition.rowDims.length === 2) {
      if (definition.colDims.length === 1) {
        // c. 2个行维度 + 1个列维度, 支持: 气泡图<br/>
        if (chartOption.chartType === "MATRIXPIE") {
          chart = charts.matrixPie3D;
          chartOption.chartType = ChartType.MATRIXPIE;
        } else {
          continue;
        }
      } else if (definition.colDims.length === 0) {
        // d. 2个行维度, 支持: 折线图/柱状图
        if (chartOption.chartType === "LINE") {
          chart = charts.lineAndBar2DFor2RowDim;
          chartOption.chartType = ChartType.LINE;
        } else if (chartOption.chartType === "BAR") {
          chart = charts.lineAndBar2DFor2RowDim;
          chartOption.chartType = ChartType.BAR;
        } else {
          continue;
        }
      }
    } else {
      continue;
    }

    const rptEntity = {
      id: rpt.id,
      label: rpt.name,
      chart,
      definition,
      chartTypeStr: chartOption.chartType.toString(),
      chartOption,
    };
    customizedReport.children.push(rptEntity);

    customizedReports.value[rpt.id] = rptEntity;
  }

  const systemReportTree = [];
  const user: any = store.getters["user/user"];
  for (let idx = 0; idx < SYSTEM_REPORT_TREE.length; idx++) {
    const node = SYSTEM_REPORT_TREE[idx];
    if (user.id === 4 && node.children) {
      for (let m = 0; m < node.children.length; m++) {
        node.children[m].definition.condition = node.children[m].definition.condition || { items: [] };
        node.children[m].definition.condition.items.push({
          name: "category1098155089330176",
          operator: "BELONG_TO",
          value1: "1098155357765632,1098155626201088",
        });
      }
    }
    systemReportTree.push(node);
  }

  reportTreeData.value = systemReportTree.concat([customizedReport]);

  let node: any = {};
  let checkedId = id;
  if (checkedId) {
    if (checkedId === 0) {
      checkedId = reportTreeData.value[0].children[0].id;
      node = reportTreeData.value[0].children[0];
    } else if (checkedId === 1000) {
      node = customizedReport;
    } else {
      node = customizedReports.value[checkedId];
    }
    if (checkedId) {
      expandedKeys.value = [checkedId >= 1000 ? 1000 : parseInt("" + checkedId / 20)];
      checkedKeys.value = [checkedId];
    }
    handleNodeClick(node);
  } else {
    currentNode.value = null;
  }
};

const handleNodeClick = async (node: any) => {
  if (node.id === null) {
    currentNode.value = null;
    return;
  }
  treeStyles.value = {};
  treeStyles.value[node.id] = {
    color: "#5D7A56",
  };

  if (!node || !node.definition) {
    return;
  }

  const datas: any = await DuduAjax.post<ReportResult>(
    { url: API_BIS_REPORT_STAT, path: { projectId: (store.state as any).project.info.id, UC: 1 } },
    node.definition
  ).catch(() => "");
  if (!datas) {
    return;
  }
  updateDataByFilter.value = false;
  currentNode.value = node;

  nextTick(() => {
    currentDefinition.value = node.definition;
    currentDatas.value = datas;
    currentReportTitle.value = node.label;
    renderChart();
  });
};

const renderChart = () => {
  currentChartOption.value = currentNode.value.chart(
    currentDefinition.value,
    currentNode.value.chartOption,
    currentDatas.value
  );
};

const reFilter = (datas: ReportResult) => {
  updateDataByFilter.value = true;
  currentDatas.value = datas;
  renderChart();
};

const switchXY = async () => {
  const newDefinition = JSON.parse(JSON.stringify(currentDefinition.value));

  if (currentNode.value.id === 5) {
    // lifeCycle
    newDefinition.measures = [currentDefinition.value.measures[1], currentDefinition.value.measures[0]];
  } else if (
    currentDefinition.value.rowDims &&
    currentDefinition.value.rowDims.length === 2 &&
    (!currentDefinition.value.colDims || currentDefinition.value.colDims.length === 0)
  ) {
    newDefinition.rowDims = [currentDefinition.value.rowDims[1], currentDefinition.value.rowDims[0]];
    newDefinition.colDims = [];
  } else if (
    currentDefinition.value.rowDims &&
    currentDefinition.value.rowDims.length === 1 &&
    currentDefinition.value.colDims &&
    currentDefinition.value.colDims.length === 1
  ) {
    newDefinition.rowDims = [currentDefinition.value.colDims[0]];
    newDefinition.colDims = [currentDefinition.value.rowDims[0]];
  } else {
    return;
  }

  const datas: any = await DuduAjax.post<ReportResult>(
    { url: API_BIS_REPORT_STAT, path: { projectId: (store.state as any).project.info.id, UC: 1 } },
    newDefinition
  ).catch(() => "");
  if (!datas) {
    return;
  }
  updateDataByFilter.value = false;

  currentDefinition.value = newDefinition;
  currentDatas.value = datas;

  renderChart();
};

const addReport = () => {
  currentReport.value = JSON.parse(JSON.stringify(initReportEntity));
  showReportEditWin.value = true;
};

const getRealDimName = (dim: any) => {
  if (dim.dateUnit) {
    if (dim.dateUnit === "YEAR") {
      return dim.name + "__y";
    }
    if (dim.dateUnit === "MONTH") {
      return dim.name + "__m";
    }
  }
  return dim.name;
};

const editReport = (node: any) => {
  const rptEntity = customizedReports.value[node.id];
  const rdim1 =
    rptEntity.definition.rowDims && rptEntity.definition.rowDims[0] ? rptEntity.definition.rowDims[0] : false;
  const rdim2 =
    rptEntity.definition.rowDims && rptEntity.definition.rowDims[1] ? rptEntity.definition.rowDims[1] : false;
  const cdim1 =
    rptEntity.definition.colDims && rptEntity.definition.colDims[0] ? rptEntity.definition.colDims[0] : false;
  const measure1 =
    rptEntity.definition.measures && rptEntity.definition.measures[0] ? rptEntity.definition.measures[0] : false;
  const chartOption = rptEntity.chartOption;
  currentReport.value = {
    id: node.id,
    name: rptEntity.label,
    rowDim1Name: rdim1 ? getRealDimName(rdim1) : "",
    rowDim1Limit: rdim1 ? rdim1.limit : 20,
    rowDim1Total: rdim1 ? rdim1.needTotal : true,
    rowDim1Sub: rdim1 ? !rdim1.self : true,
    rowDim2Name: rdim2 ? getRealDimName(rdim2) : "",
    rowDim2Limit: rdim2 ? rdim2.limit : 20,
    rowDim2Total: rdim2 ? rdim2.needTotal : true,
    rowDim2Sub: rdim1 ? !rdim2.self : true,
    colDim1Name: cdim1 ? getRealDimName(cdim1) : "",
    colDim1Limit: cdim1 ? cdim1.limit : 20,
    colDim1Total: cdim1 ? cdim1.needTotal : true,
    colDim1Sub: cdim1 ? !cdim1.self : true,
    measure1Name: measure1 ? measure1.name : "count",
    measure1MinValue: measure1 ? measure1.minValue : 0,
    chartType: chartOption ? ChartType[chartOption.chartType] : "",
    chartTitle: chartOption ? chartOption.title : "",
    chartSubTitle: chartOption ? chartOption.subTitle : "",
    xaxisTitle: chartOption ? chartOption.xaxisTitle : "",
    yaxisTitle: chartOption ? chartOption.yaxisTitle : "",
    hideTitle: chartOption ? chartOption.hideTitle : false,
    hideAxisTitle: chartOption ? chartOption.hideAxisTitle : true,
    hideLabel: chartOption ? chartOption.hideLabel : false,
    hideLegend: chartOption ? chartOption.hideLegend : false,
    hideToolbox: chartOption ? chartOption.hideToolbox : false,
    condition: JSON.stringify(
      rptEntity.definition.condition ? rptEntity.definition.condition : { items: [], relation: "" }
    ),
  };
  showReportEditWin.value = true;
};

const deleteReport = async (node: any, data: any) => {
  const confirmed: any = await showConfirm("确定删除报表: " + data.label + "?").catch(() => "");
  if (!confirmed) {
    return;
  }
  await DuduAjax.delete(
    { url: API_BIS_REPORT_CUSTOMIZED_WITH_ID, path: { projectId: (store.state as any).project.info.id, id: data.id } },
    data.id
  );
  showSuccess("删除成功");
  initReportTree(1000);
};

const getDimDef = (name: string) => {
  for (let i = 0; i < dimGroups.value.length; i++) {
    const dims = dimGroups.value[i].dims;
    for (let j = 0; j < dims.length; j++) {
      if (dims[j].rname === name) {
        return JSON.parse(JSON.stringify(dims[j]));
      }
    }
  }
  return null;
};

const getMeasureDef = (name: string) => {
  for (let j = 0; j < allMeasures.value.length; j++) {
    if (allMeasures.value[j].name === name) {
      return JSON.parse(JSON.stringify(allMeasures.value[j]));
    }
  }
  return null;
};

const saveReport = async () => {
  const isValid = await reportForm.value.validate().catch(() => false);
  if (!isValid) {
    return;
  }

  const isChartValid = await reportChartForm.value.validate().catch(() => false);
  if (!isChartValid) {
    return;
  }

  saveReport2();
};

const saveReport2 = async () => {
  const rowDims: Array<any> = [];
  if (currentReport.value.rowDim1Name !== "") {
    const dim: any = getDimDef(currentReport.value.rowDim1Name);
    if (dim) {
      delete dim.rname;
      dim.limit = currentReport.value.rowDim1Limit;
      dim.needTotal = currentReport.value.rowDim1Total;

      if (dim.name.indexOf("category") === 0) {
        dim.self = !currentReport.value.rowDim1Sub;
      }

      rowDims.push(dim);
    }
  }

  if (currentReport.value.rowDim2Name !== "") {
    const dim: any = getDimDef(currentReport.value.rowDim2Name);
    if (dim) {
      delete dim.rname;
      dim.limit = currentReport.value.rowDim2Limit;
      dim.needTotal = currentReport.value.rowDim2Total;

      if (dim.name.indexOf("category") === 0) {
        dim.self = !currentReport.value.rowDim2Sub;
      }

      rowDims.push(dim);
    }
  }

  const colDims: Array<any> = [];
  if (currentReport.value.colDim1Name !== "") {
    const dim: any = getDimDef(currentReport.value.colDim1Name);
    if (dim) {
      delete dim.rname;
      dim.limit = currentReport.value.colDim1Limit;
      dim.needTotal = currentReport.value.colDim1Total;

      if (dim.name.indexOf("category") === 0) {
        dim.self = !currentReport.value.colDim1Sub;
      }

      colDims.push(dim);
    }
  }

  const measures: Array<any> = [];
  if (currentReport.value.measure1Name !== "") {
    const measure: any = getMeasureDef(currentReport.value.measure1Name);
    if (measure) {
      measure.minValue = currentReport.value.measure1MinValue;
      measures.push(measure);
    }
  }

  const definition = {
    rowDims,
    colDims,
    measures,
    condition: JSON.parse(advt.value.getSearchData()) || { items: [], relation: "" },
  };

  const chartOption = {
    chartType: currentReport.value.chartType,
    title: currentReport.value.chartTitle,
    subTitle: currentReport.value.chartSubTitle,
    xaxisTitle: currentReport.value.xaxisTitle,
    yaxisTitle: currentReport.value.yaxisTitle,
    hideTitle: currentReport.value.hideTitle,
    hideAxisTitle: currentReport.value.hideAxisTitle,
    hideLabel: currentReport.value.hideLabel,
    hideLegend: currentReport.value.hideLegend,
    hideToolbox: currentReport.value.hideToolbox,
  };

  const rpt = {
    name: currentReport.value.name,
    definition: JSON.stringify(definition),
    chartOption: JSON.stringify(chartOption),
  };
  if (currentReport.value.id > 0) {
    await DuduAjax.put(
      { url: API_BIS_REPORT_CUSTOMIZED_WITH_ID, path: { projectId: projectId.value, id: currentReport.value.id } },
      rpt
    );
    showSuccess("保存成功");
    saveReportCallback(currentReport.value.id);
  } else {
    const id = await DuduAjax.post<number>(
      { url: API_BIS_REPORT_CUSTOMIZED, path: { projectId: projectId.value } },
      rpt
    );
    showSuccess("保存成功");
    saveReportCallback(id);
  }
};

const saveReportCallback = (id: number) => {
  initReportTree(id);
  colseEditReport();
};

const navToList = () => {
  router.replace({ name: "patent-list", params: { projectId: projectId.value } } as any);
};

initReportTree();
loadReportModel();
loadCategoryTree();

return { __sfc: true,store, router, reportTree, reportForm, reportChartForm, advt, reportTreeData, defaultProps, expandedKeys, checkedKeys, treeStyles, currentNode, currentChartOption, currentDefinition, currentReportTitle, currentDatas, appendFields, updateDataByFilter, showReportEditWin, currentReport, initReportEntity, dimGroups, allMeasures, customizedReports, reportEditStep, helpText, projectId, chartTypes, reportRules, colseEditReport, editReportPreStep, editReportNextStep, onReportNameChange, loadCategoryTree, loadReportModel, initReportTree, handleNodeClick, renderChart, reFilter, switchXY, addReport, getRealDimName, editReport, deleteReport, getDimDef, getMeasureDef, saveReport, saveReport2, saveReportCallback, navToList, ReportChart, ReportTable, ReportFilter, AdvSearch, StatisticsPreview }
}

})